import { ReactNode } from 'react';
import type { DrawnCellLabels } from '../cells';
import { AggRenderContext, ColumnGroup } from '../types';
import type { AccumulationColumnSpec, AccumulationCtx } from '../../accumulationTimeline/types';

export interface PresentationCtx {
  terms: ClaimTerms;
  yearSnapshots: YearSnapshot[];
}

export interface StrategyPresentation {
  // Optional strategy override for the Benefit amount cell, used when the
  // per-row cap logic differs from the year-snapshot headline.
  benefitAmountCell?: (ctx: {
    month: MonthlyEntry;
    snapshot: YearSnapshot | undefined;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Optional numeric override for the Benefit amount value per row. When
  // provided, aggregates in the Benefit amount column use this value.
  benefitAmountValueForMonth?: (ctx: {
    month: MonthlyEntry;
    snapshot: YearSnapshot | undefined;
    terms: ClaimTerms;
  }) => number | null | undefined;

  // Extra column groups appended to the universal payout table after the
  // standard When / Cost / Benefit / Payout / Pool / DB groups.
  extraPayoutColumnGroups?: (ctx: PresentationCtx) => ColumnGroup[];

  // Columns for the pre-care Accumulation Timeline table. When provided, fully
  // replaces the universal default (`buildDefaultAccumulationColumns`) — a
  // strategy returns the column set whose headline balances match its product
  // (e.g. an annuity surfaces its Contract / Account Value, CM II surfaces its
  // escalating rider). Omit to use the universal default.
  accumulationColumns?: (ctx: AccumulationCtx) => AccumulationColumnSpec[];

  // Sub-rows rendered inside the universal PaidCell, below the headline
  // payout amount. Returns null when this strategy has no extras for this
  // month. Strategy owns its own casts into `month.deltas.strategyDeltas`
  // and `month.state.strategyState`.
  paidCellExtras?: (month: MonthlyEntry) => ReactNode;

  // Per-cell tooltip content for the universal LTC Pool cell. When provided,
  // the cell wraps in a <Tooltip>. Return null to suppress the hover on
  // rows where there's nothing meaningful to show. Distinct from
  // `copy.poolTooltip` which is the column-header description.
  poolCellTooltip?: (ctx: {
    month: MonthlyEntry;
    openState: MonthlyState;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Optional full-cell override for the universal LTC Pool cell, replacing
  // LtcPoolWithDeltaCell entirely. Use when the running dollar balance is not
  // the product's real constraint — e.g. a day-denominated product whose
  // sentinel pool would render a misleading "Unlimited" while the true limit
  // is a day counter. The returned node renders as-is (wrap it in a Tooltip
  // yourself; `poolCellTooltip` is only consumed by the default cell). Pair
  // with `columnLabels.pool` / `poolCellBlankKey` / `poolAggregateCell` so
  // the header, dim-unchanged mode, and Total row match. Return null to fall
  // back to the default cell for a given row.
  poolCell?: (ctx: {
    month: MonthlyEntry;
    openState: MonthlyState;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Row-collapse key for a custom `poolCell` (see ColumnSpec.blankKey).
  // Defaults to the universal pool balance / 'unlimited' sentinel key, which
  // would fade EVERY row of an unlimited product — a custom poolCell showing
  // a moving balance should key off that balance instead.
  poolCellBlankKey?: (month: MonthlyEntry) => number | string | null;

  // Total-row override for the pool column, paired with `poolCell` (the
  // default aggregate renders the dollar balance / "Unlimited").
  poolAggregateCell?: (ctx: AggRenderContext & { terms: ClaimTerms }) => ReactNode | null;

  // Per-cell tooltip content for the universal Death Benefit cell.
  deathBenefitCellTooltip?: (ctx: {
    month: MonthlyEntry;
    openState: MonthlyState;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Per-cell tooltip for the PaidCell, explaining why this row's payout
  // is what it is (cost vs cap, pool exhaustion, reimbursement rule, etc.).
  // Returns null when there's nothing to explain (e.g., a $0 row in a phase
  // that hasn't started paying).
  payoutCellTooltip?: (ctx: { month: MonthlyEntry; terms: ClaimTerms }) => ReactNode | null;

  // Extra content appended to the BenefitAmountCell's inflation tooltip.
  // Lets a strategy explain how this year's monthly cap relates to other
  // policy facts (for Asset Flex: the at-issue Acceleration pool derivation).
  benefitAmountCellTooltipFooter?: (ctx: {
    snapshot: YearSnapshot;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Optional strategy override for the theoretical pool-limit cell. Use this
  // when the spend-independent pool schedule is not a simple at-issue pool ×
  // inflation factor, so the generic PoolLimitCell tooltip would explain the
  // right number with the wrong math.
  poolLimitCell?: (ctx: {
    snapshot: YearSnapshot | undefined;
    terms: ClaimTerms;
  }) => ReactNode | null;

  // Override for the rate displayed in the BenefitAmountCell's inflation
  // tooltip. Defaults to `terms.inflationRate`. Asset Flex uses this to
  // surface the On-Face ACIO override (`baseBenefitPoolInflationRate`)
  // when it differs from the policy-wide ACIO, so the tooltip's
  // base × (1+r)^n math agrees with the actual monthlyBenefitCap.
  benefitInflationRate?: (terms: ClaimTerms) => number;

  // Override the body labels / description of the "Drawn to date" cell tooltip.
  // Single-pool products (traditional LTC) use this to drop AOB/COB framing.
  drawnCellLabels?: DrawnCellLabels;

  // Override universal column labels while keeping the same column behavior.
  // Useful where a shared engine slot has product-specific semantics, e.g.
  // AnnuityCare II stores residual Accumulated Value in the death-benefit slot.
  columnLabels?: Partial<Record<UniversalColumnKey, string>>;

  // Universal column keys this strategy wants suppressed entirely (e.g. a
  // traditional LTC policy has no death benefit; CM II's pool limit is flat
  // and adds no information). Filtering happens in `buildPayoutColumnGroups`.
  hiddenColumns?: ReadonlyArray<UniversalColumnKey>;

  // Copy overrides on universal cells. Universal defaults win when absent.
  // Header tooltips are ReactNode so each strategy can render a formatted
  // formula block (see HeaderTooltip); plain strings still work as a
  // fallback for simple copy.
  copy?: {
    // Function form receives the claim terms so header copy can branch on
    // policy config (e.g. simple vs compound rider accrual — P2-24).
    poolTooltip?: ReactNode | ((terms: ClaimTerms) => ReactNode);
    payoutColumnTooltip?: ReactNode;
    benefitInflationCompoundLabel?: string;
    benefitInflationCapNote?: (capYear: number) => string;
    // Header tooltips for the rest of the universal columns. Strategies that
    // share the universal formula leave these undefined; products that diverge
    // (e.g. traditional LTC: no AOB/COB pool, no death benefit) override here
    // so the header explanation matches the cell tooltips.
    costTooltip?: ReactNode;
    benefitAmountTooltip?: ReactNode;
    poolLimitTooltip?: ReactNode;
    drawnTooltip?: ReactNode;
    deathBenefitTooltip?: ReactNode;
  };
}

// The fixed set of column keys assigned by `buildPayoutColumnGroups`. Strategies
// can reference these when declaring `hiddenColumns`. Strategy-appended extras
// live outside this universe and aren't toggleable here.
export type UniversalColumnKey =
  | 'when'
  | 'cost'
  | 'benefit-amount'
  | 'payout'
  | 'pool'
  | 'pool-limit'
  | 'pool-drawn'
  | 'death-benefit';

export const defaultPresentation: StrategyPresentation = {};
